import React, { useState, useEffect } from "react";
import { AdminSideMenu } from "./AdminMenu";
import API_URL from "../../api/API";
import { useAdminAuthContext } from "./useAdminAuthContext";
import { Link } from "react-router-dom";

export const AdminManage = () => {
  const { admin } = useAdminAuthContext();

  const [adminData, setAdminData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchAdminData = async (page) => {
    try {
      setIsLoading(true);
      setError("");
      const response = await fetch(API_URL.GET_ADMIN_ALL + `?page=${page}&limit=50`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${admin.token}`,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch data");
      }

      setAdminData(data.users);
      setFilteredData(data.users);
      setCurrentPage(data.page);
      setTotalPages(data.totalPages);
      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  const deleteAdmin = async (id) => {
    try {
      setIsDeleting(true);
      const response = await fetch(API_URL.DELETE_ADMIN + `${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${admin.token}`,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to delete admin member");
      }

      fetchAdminData(currentPage);
      alert("Admin member deleted successfully!");
    } catch (err) {
      alert(`Error deleting admin: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this admin member?")) {
      deleteAdmin(id);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim() === "") {
      setFilteredData(adminData);
    } else {
      const lowerQuery = query.toLowerCase();
      const filtered = adminData.filter(
        (admin) =>
          admin.firstname.toLowerCase().includes(lowerQuery) ||
          admin.lastname.toLowerCase().includes(lowerQuery) ||
          admin.email.toLowerCase().includes(lowerQuery)
      );
      setFilteredData(filtered);
    }
  };

  useEffect(() => {
    fetchAdminData(currentPage);
  }, [currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="flex h-screen">
      <AdminSideMenu />

      <div className="flex-1 p-6 bg-gray-100 pt-16 flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Manage Admin</h1>
          <Link
            to="/administrator/admin/create"
            className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-600"
          >
            Create Admin Account
          </Link>
        </div>

        <div className="flex justify-end mb-4">
          <input
            type="text"
            placeholder="Search by first name, last name, or email"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-1/3 p-2 border border-gray-300 rounded"
          />
        </div>

        <div className="flex-1 overflow-auto border rounded shadow bg-white">
          <table className="table-auto w-full">
            <thead className="sticky top-0 bg-gray-200">
              <tr>
                <th className="px-4 py-2 text-left">First Name</th>
                <th className="px-4 py-2 text-left">Last Name</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Role</th> {/* Added Role column */}
                <th className="px-4 py-2 text-left">Created At</th>
                <th className="px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    <p className="text-blue-600">Loading data...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    <p className="text-red-600">{error}</p>
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    <p>No records found</p>
                  </td>
                </tr>
              ) : (
                filteredData.map((admin, index) => (
                  <tr
                    key={admin._id}
                    className={
                      index % 2 === 0
                        ? "bg-white hover:bg-gray-100 border-b border-gray-200"
                        : "bg-gray-50 hover:bg-gray-100 border-b border-gray-200"
                    }
                  >
                    <td className="px-4 py-2">{admin.firstname}</td>
                    <td className="px-4 py-2">{admin.lastname}</td>
                    <td className="px-4 py-2">{admin.email}</td>
                    <td className="px-4 py-2">{admin.role}</td> {/* Displaying role here */}
                 
                    <td className="px-4 py-2">
                      {new Date(admin.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => handleDelete(admin._id)}
                        className="px-2 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                        disabled={isDeleting}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded ${
              currentPage === 1
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Previous
          </button>

          <p className="text-sm">
            Page {currentPage} of {totalPages}
          </p>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded ${
              currentPage === totalPages
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};
